import { z } from 'zod';
import {
  baseDocumentFields,
  httpUrl,
  moneyCents,
  objectIdString,
  slug,
  stateCode,
} from './common.js';
import { propertyKind, propertyStage, propertyStatus, propertyType } from './enums.js';

export const geoPoint = z
  .object({
    type: z.literal('Point'),
    /** GeoJSON: [longitude, latitude] (in that order). */
    coordinates: z.tuple([z.number(), z.number()]),
  })
  .strict();

export type GeoPoint = z.infer<typeof geoPoint>;

export const priceRange = z
  .object({
    min: moneyCents,
    max: moneyCents,
  })
  .strict()
  .refine((r) => r.max >= r.min, { message: 'max must be >= min' });

export type PriceRange = z.infer<typeof priceRange>;

export const unitType = z
  .object({
    label: z.string().min(1).max(60),
    bedrooms: z.number().int().min(0).max(10),
    bathrooms: z.number().int().min(0).max(10),
    areaSqm: z.number().nonnegative().max(1000).optional(),
    parking: z.number().int().min(0).max(10).optional(),
  })
  .strict();

export type UnitType = z.infer<typeof unitType>;

export const propertySpecs = z
  .object({
    bedrooms: z.number().int().min(0).max(20).optional(),
    bathrooms: z.number().int().min(0).max(20).optional(),
    parking: z.number().int().min(0).max(20).optional(),
    areaSqm: z.number().positive().max(100_000).optional(),
  })
  .strict();

export type PropertySpecs = z.infer<typeof propertySpecs>;

export const property = z
  .object({
    ...baseDocumentFields,
    /** The broker who owns this property card. Slug is unique per broker, not global. */
    brokerId: objectIdString,
    name: z.string().trim().min(2).max(120),
    slug: slug,
    /** Short emotional hook shown under the name on cards (e.g., "Pertinho do mar"). */
    tagline: z.string().trim().max(140).optional(),
    developer: z.string().trim().max(120).optional(),
    description: z.string().max(4000).optional(),
    city: z.string().trim().min(2).max(80),
    state: stateCode,
    neighborhood: z.string().trim().min(2).max(80).optional(),
    location: geoPoint.optional(),
    /** null = sem preço divulgado (o corretor limpou). */
    priceRange: priceRange.nullable().optional(),
    unitTypes: z.array(unitType).max(20).default([]),
    photos: z.array(httpUrl).max(30).default([]),
    features: z.array(z.string().trim().min(1).max(60)).max(40).default([]),
    /** null = sem selo (o corretor limpou no painel). */
    stage: propertyStage.nullable().optional(),
    kind: propertyKind.optional(),
    propertyType: propertyType.nullable().optional(),
    /** Características do imóvel avulso (empreendimento usa unitTypes). */
    specs: propertySpecs.optional(),
    /** Código interno do corretor (ex: "5950"). */
    referenceCode: z.string().trim().max(30).optional(),
    /** Aparece primeiro e com destaque na página pública. */
    featured: z.boolean().optional(),
    status: propertyStatus,
  })
  .strict();

export type Property = z.infer<typeof property>;
